/**
 * pijplijn.js — de vier stappen van een verwerking, afgeleid uit het detail van /admin/analysis.
 *
 * Dit staat los van het component omdat hier BESLIST wordt: welke stap is mislukt, welke is
 * overgeslagen, en van welke is niets vastgelegd. Dat zijn drie verschillende dingen die er in
 * beeld bijna hetzelfde uitzien, en ze zijn alleen te toetsen als ze niet in een Lit-template
 * verstopt zitten.
 *
 * 'onbekend' is nadrukkelijk geen fout. Een verwerking van vóór deze meting heeft geen logboek;
 * die hoort er grijs uit te zien en niet rood, want er is niets misgegaan -- we weten het alleen
 * niet. Een lege waarde en een onbekende waarde moeten uit elkaar te houden blijven, anders gaat
 * iemand een probleem zoeken dat er niet is.
 */
export function fmtMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1).replace('.', ',')}s`;
}

export function fmtBytes(b) {
  if (b == null || !Number.isFinite(b)) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export function fmtGeld(v) {
  if (v == null || !Number.isFinite(v)) return null;
  return `$${v.toFixed(v < 0.01 ? 4 : 3).replace('.', ',')}`;
}

export function fmtGetal(n) {
  return n == null || !Number.isFinite(n) ? null : n.toLocaleString('nl-NL');
}

/** mm:ss uit seconden — de duur van de audio, als het model die heeft teruggegeven. */
export function fmtDuur(sec) {
  if (sec == null || !Number.isFinite(sec)) return null;
  const m = Math.floor(sec / 60);
  return `${m}:${String(Math.round(sec - m * 60)).padStart(2, '0')}`;
}

/**
 * De stappenlijst links. Elke stap weet zijn eigen staat, en die komt uit de gegevens: 'fout'
 * als die stap is mislukt, 'overgeslagen' als hij door een eerdere fout nooit is gedraaid, en
 * 'onbekend' als er van die stap niets is vastgelegd. Dat laatste is geen fout -- het is een
 * run van vóór deze meting -- en hoort er dus ook niet als fout uit te zien.
 */
export function stappen(d) {
  const meta = d.run_meta || null;
  const tm = d.transcript_meta || meta?.transcriptie || null;
  const ex = meta?.extractie || null;
  const r = d.recording;

  const transcriptieMislukt = tm ? tm.gelukt === false : (r.status === 'error' && !d.transcript);
  const extractieMislukt = !!ex?.fout || (d.run?.status === 'error' && !transcriptieMislukt);
  const uitkomstAantal = d.domain_result ? 1 : (d.borings?.length || 0);

  const persistMs = meta?.tijden?.extractFinished && meta?.tijden?.persistFinished
    ? new Date(meta.tijden.persistFinished) - new Date(meta.tijden.extractFinished)
    : null;

  const pogingTekst = (lijst) => {
    if (!lijst?.length) return '';
    const n = lijst.length;
    return `${n} poging${n === 1 ? '' : 'en'}`;
  };

  return [
    {
      id: 'audio', n: 1, titel: 'Audio',
      sub: [r.filename, fmtDuur(tm?.usage?.seconds)].filter(Boolean).join(' · '),
      duur: null, staat: 'ok', notitie: '', notitieKleur: 'var(--mv-text-sub,#6c757d)',
    },
    {
      id: 'transcriptie', n: 2, titel: 'Transcriptie',
      sub: [tm?.model, tm?.gehoordeTaal || tm?.taalMeegestuurd].filter(Boolean).join(' · '),
      duur: fmtMs(tm?.ms),
      staat: transcriptieMislukt ? 'fout' : tm || d.transcript ? 'ok' : 'onbekend',
      notitie: [pogingTekst(tm?.pogingen), tm?.woordenlijst ? 'woordenlijst' : ''].filter(Boolean).join(' · '),
      notitieKleur: (tm?.pogingen?.length || 0) > 1 ? '#b45309' : 'var(--mv-text-sub,#6c757d)',
    },
    {
      id: 'extractie', n: 3, titel: 'Extractie',
      sub: [ex?.model || d.ai_config?.extractionModel, d.run?.prompt_title || d.ai_config?.promptTitle].filter(Boolean).join(' · '),
      duur: fmtMs(ex?.ms),
      // Geen logboek betekent niet 'onbekend' als er wél een uitkomst ligt: dan heeft deze stap
    // aantoonbaar gedraaid, we weten alleen niet hoe lang of met hoeveel pogingen.
    staat: extractieMislukt ? 'fout' : transcriptieMislukt ? 'overgeslagen'
      : (ex || d.result_data || uitkomstAantal) ? 'ok' : 'onbekend',
      notitie: pogingTekst(ex?.pogingen),
      notitieKleur: (ex?.pogingen?.length || 0) > 1 ? 'var(--mv-danger,#dc3545)' : 'var(--mv-text-sub,#6c757d)',
    },
    {
      id: 'uitkomst', n: 4, titel: 'Uitkomst',
      sub: (extractieMislukt || transcriptieMislukt) ? 'Niets weggeschreven'
        : d.domain_result ? `Boring ${d.domain_result.boringName || '?'}`
        : uitkomstAantal ? `${uitkomstAantal} boring${uitkomstAantal === 1 ? '' : 'en'}` : 'Geen boringen',
      duur: fmtMs(persistMs),
      staat: extractieMislukt || transcriptieMislukt ? 'overgeslagen' : uitkomstAantal ? 'ok' : 'onbekend',
      // Hoeveel databaseverkeer het wegschrijven kostte. Online is elke query een netwerkretour;
      // dat is waar de tijd van deze stap heen gaat, niet in de SQL. Zie db-meter.js (backend).
      notitie: dbNotitie(meta?.tijden?.db?.uitkomst), notitieKleur: 'var(--mv-text-sub,#6c757d)',
    },
  ];
}

/** "12 query's · 1,9s database" — of leeg als deze run nog niet gemeten werd. */
export function dbNotitie(db) {
  if (!db || typeof db.queries !== 'number') return '';
  return `${db.queries} ${db.queries === 1 ? 'query' : "query's"} · ${fmtMs(db.ms)} database`;
}

/** De stap die standaard openstaat: de mislukte als er een is, anders de transcriptie. */
export function standaardStap(lijst) {
  return (lijst.find((s) => s.staat === 'fout') || lijst.find((s) => s.id === 'transcriptie')).id;
}

export function stapKleuren(staat) {
  return {
    ok: ['rgba(34,197,94,0.15)', '#15803d'],
    fout: ['rgba(220,53,69,0.12)', 'var(--mv-danger,#dc3545)'],
    overgeslagen: ['var(--mv-border,#e9ecef)', 'var(--mv-text-sub,#6c757d)'],
    onbekend: ['var(--mv-border,#e9ecef)', 'var(--mv-text-sub,#adb5bd)'],
  }[staat] || ['var(--mv-border,#e9ecef)', 'var(--mv-text-sub,#6c757d)'];
}
